import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';

type RangeValue = { lower: number; upper: number };

@Component({
  selector: 'ind-range-slider',
  templateUrl: 'ind-range-slider.html',
  styleUrls: ['ind-range-slider.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule, FormsModule, TranslateModule],
})
export class IndRangeSliderComponent implements OnChanges, OnDestroy {
  @Input() rangeConfig: {
    min: number;
    max: number;
    step: number;
    currentMin: number;
    currentMax: number;
    unit?: string;
  };
  @Input() showInputs = true;
  @Output() onRangeChange = new EventEmitter<RangeValue>();

  get tickValues(): number[] {
    const min = this.rangeConfig?.min ?? 0;
    const max = this.rangeConfig?.max ?? 100;
    return [0, 1, 2, 3, 4].map(i => Math.round(min + (i * (max - min)) / 4));
  }

  public rangeValue: RangeValue = { lower: 0, upper: 100 };
  public minInput = 0;
  public maxInput = 100;

  private _inputDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.rangeConfig && this.rangeConfig) {
      const normalized = this._normalizeRange(
        this.rangeConfig.currentMin ?? this.rangeConfig.min,
        this.rangeConfig.currentMax ?? this.rangeConfig.max,
      );
      this.rangeValue = normalized;
      this.minInput = normalized.lower;
      this.maxInput = normalized.upper;
    }
  }

  ngOnDestroy(): void {
    if (this._inputDebounceTimer !== null) {
      clearTimeout(this._inputDebounceTimer);
    }
  }

  public onSliderChange(event: CustomEvent): void {
    const value = event?.detail?.value as RangeValue;
    const normalized = this._normalizeRange(value?.lower, value?.upper);
    this._setAndEmit(normalized);
  }

  // D6: secondary path — fires while typing; guards against ionBlur not firing on Android
  public onMinInput(): void {
    this._scheduleApply();
  }

  public onMaxInput(): void {
    this._scheduleApply();
  }

  public onMinBlur(): void {
    this._cancelScheduled();
    this._applyInputRange();
  }

  public onMaxBlur(): void {
    this._cancelScheduled();
    this._applyInputRange();
  }

  public formatPin = (value: number): string => `${value}${this.rangeConfig?.unit ?? ''}`;

  private _scheduleApply(): void {
    this._cancelScheduled();
    this._inputDebounceTimer = setTimeout(() => {
      this._inputDebounceTimer = null;
      this._applyInputRange();
    }, 300);
  }

  private _cancelScheduled(): void {
    if (this._inputDebounceTimer !== null) {
      clearTimeout(this._inputDebounceTimer);
      this._inputDebounceTimer = null;
    }
  }

  private _applyInputRange(): void {
    const normalized = this._normalizeRange(this.minInput, this.maxInput);
    this._setAndEmit(normalized);
  }

  private _setAndEmit(value: RangeValue): void {
    this.rangeValue = value;
    this.minInput = value.lower;
    this.maxInput = value.upper;
    this.onRangeChange.emit(value);
  }

  // D3: round to step before clamping so decimal input (e.g. "50.5" on iOS) is snapped to the
  // nearest valid step value rather than producing a fractional pill bound.
  private _normalizeRange(lower: number, upper: number): RangeValue {
    const min = this.rangeConfig?.min ?? 0;
    const max = this.rangeConfig?.max ?? 100;
    const step = this.rangeConfig?.step ?? 1;
    const normalizedLower = this._clampNumber(Math.round(Number(lower) / step) * step, min, max);
    const normalizedUpper = this._clampNumber(Math.round(Number(upper) / step) * step, min, max);
    return {
      lower: Math.min(normalizedLower, normalizedUpper),
      upper: Math.max(normalizedLower, normalizedUpper),
    };
  }

  private _clampNumber(value: number, min: number, max: number): number {
    if (Number.isNaN(value)) {
      return min;
    }
    return Math.min(Math.max(value, min), max);
  }
}
